import React, { useEffect, useState } from "react";
import { Loader2, Save, Shield, Building2, Target, Key, Users as UsersIcon, PartyPopper, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import UsersTab from "@/pages/UsersTab";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { parseCurrencyInput, formatBRL } from "@/lib/format";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Password tab state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setSettings(res.data))
      .catch(() => toast.error("Erro ao carregar configurações"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await api.put("/settings", settings);
      setSettings(res.data);
      toast.success("Configurações salvas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não conferem");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Senha alterada com sucesso");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setPwSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const setCompany = (field, value) =>
    setSettings((s) => ({ ...s, company: { ...s.company, [field]: value } }));
  const setGoals = (field, value) =>
    setSettings((s) => ({ ...s, goals: { ...s.goals, [field]: value } }));
  const setWoovi = (field, value) =>
    setSettings((s) => ({ ...s, woovi: { ...s.woovi, [field]: value } }));
  const setThankyou = (field, value) =>
    setSettings((s) => ({
      ...s,
      thankyou: { ...(s.thankyou || {}), [field]: value },
    }));

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
          Sistema
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-bold">Configurações</h1>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="bg-card border border-border h-auto flex-wrap">
          <TabsTrigger value="company" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-company">
            <Building2 className="w-3.5 h-3.5" /> Empresa
          </TabsTrigger>
          <TabsTrigger value="goals" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-goals">
            <Target className="w-3.5 h-3.5" /> Metas
          </TabsTrigger>
          <TabsTrigger value="woovi" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-woovi">
            <Key className="w-3.5 h-3.5" /> Woovi
          </TabsTrigger>
          <TabsTrigger value="thankyou" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-thankyou">
            <PartyPopper className="w-3.5 h-3.5" /> Obrigado
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-security">
            <Shield className="w-3.5 h-3.5" /> Segurança
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="users" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-users">
              <UsersIcon className="w-3.5 h-3.5" /> Usuários
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="company">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display">Dados da empresa (nota / recibo)</CardTitle>
              <p className="text-xs text-muted-foreground">
                Estes dados aparecem no PDF do recibo de serviço.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Razão Social</Label>
                  <Input
                    data-testid="company-name"
                    value={settings.company?.name || ""}
                    onChange={(e) => setCompany("name", e.target.value)}
                    className="bg-secondary/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">CNPJ/CPF</Label>
                  <Input
                    data-testid="company-document"
                    value={settings.company?.document || ""}
                    onChange={(e) => setCompany("document", e.target.value)}
                    className="bg-secondary/40"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Endereço completo</Label>
                <Textarea
                  data-testid="company-address"
                  value={settings.company?.address || ""}
                  onChange={(e) => setCompany("address", e.target.value)}
                  rows={3}
                  className="bg-secondary/40"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">E-mail</Label>
                  <Input
                    data-testid="company-email"
                    value={settings.company?.email || ""}
                    onChange={(e) => setCompany("email", e.target.value)}
                    className="bg-secondary/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Telefone</Label>
                  <Input
                    data-testid="company-phone"
                    value={settings.company?.phone || ""}
                    onChange={(e) => setCompany("phone", e.target.value)}
                    className="bg-secondary/40"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Nome do serviço (no recibo)</Label>
                <Input
                  data-testid="service-name"
                  value={settings.service_name || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, service_name: e.target.value }))}
                  className="bg-secondary/40"
                />
              </div>
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-company-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Salvar</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="goals">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display">Metas de faturamento</CardTitle>
              <p className="text-xs text-muted-foreground">
                Defina quanto você quer faturar por mês e por semana. O painel mostra o progresso automaticamente.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Meta mensal (R$)</Label>
                  <Input
                    data-testid="goal-monthly-input"
                    placeholder="5000,00"
                    defaultValue={((settings.goals?.monthly_cents || 0) / 100).toFixed(2).replace(".", ",")}
                    onBlur={(e) =>
                      setGoals("monthly_cents", Math.round(parseCurrencyInput(e.target.value) * 100))
                    }
                    className="h-12 text-lg font-display font-bold bg-secondary/40"
                  />
                  <p className="text-xs text-muted-foreground font-mono-num">
                    Atual: {formatBRL(settings.goals?.monthly_cents || 0)}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Meta semanal (R$)</Label>
                  <Input
                    data-testid="goal-weekly-input"
                    placeholder="1200,00"
                    defaultValue={((settings.goals?.weekly_cents || 0) / 100).toFixed(2).replace(".", ",")}
                    onBlur={(e) =>
                      setGoals("weekly_cents", Math.round(parseCurrencyInput(e.target.value) * 100))
                    }
                    className="h-12 text-lg font-display font-bold bg-secondary/40"
                  />
                  <p className="text-xs text-muted-foreground font-mono-num">
                    Atual: {formatBRL(settings.goals?.weekly_cents || 0)}
                  </p>
                </div>
              </div>
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-goals-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Salvar metas</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="woovi">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display">Credenciais Woovi</CardTitle>
              <p className="text-xs text-muted-foreground">
                Enquanto suas credenciais não estão aprovadas, mantenha o Modo Simulação ligado para testar o fluxo.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-secondary/30">
                <div>
                  <p className="text-sm font-medium">Modo Simulação</p>
                  <p className="text-xs text-muted-foreground">
                    Quando ativo, cobranças e saques são mockados (para testes).
                  </p>
                </div>
                <Switch
                  data-testid="simulation-switch"
                  checked={!!settings.woovi?.simulation_mode}
                  onCheckedChange={(v) => setWoovi("simulation_mode", v)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">App ID / API Key</Label>
                <Input
                  data-testid="woovi-appid"
                  type="password"
                  placeholder="ClientId:ClientSecret base64 ou App ID Woovi"
                  value={settings.woovi?.app_id || ""}
                  onChange={(e) => setWoovi("app_id", e.target.value)}
                  className="bg-secondary/40 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Webhook Secret (opcional)</Label>
                <Input
                  data-testid="woovi-secret"
                  value={settings.woovi?.webhook_secret || ""}
                  onChange={(e) => setWoovi("webhook_secret", e.target.value)}
                  className="bg-secondary/40 font-mono"
                />
              </div>
              <div className="rounded-lg border border-border p-3 bg-secondary/20">
                <p className="text-xs text-muted-foreground">
                  URL do webhook para configurar na Woovi:
                </p>
                <code className="text-xs text-primary break-all">
                  {process.env.REACT_APP_BACKEND_URL}/api/webhooks/woovi
                </code>
              </div>
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-woovi-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Salvar credenciais</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="thankyou">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display">Página de Obrigado pública</CardTitle>
              <p className="text-xs text-muted-foreground">
                Configure o texto e o WhatsApp que aparecerão após uma venda confirmada pela Lastlink. Cada abertura da página gera um token único de autenticidade.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-border p-3 bg-secondary/20 flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">URL para redirecionar na Lastlink:</p>
                  <code className="text-xs text-primary break-all">
                    {process.env.REACT_APP_BACKEND_URL}/obrigado
                  </code>
                </div>
                <Button asChild variant="outline" size="sm">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/obrigado`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preview-thankyou-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />
                    Visualizar
                  </a>
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Título</Label>
                  <Input
                    data-testid="ty-title"
                    value={settings.thankyou?.title || ""}
                    onChange={(e) => setThankyou("title", e.target.value)}
                    className="bg-secondary/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Subtítulo</Label>
                  <Input
                    data-testid="ty-subtitle"
                    value={settings.thankyou?.subtitle || ""}
                    onChange={(e) => setThankyou("subtitle", e.target.value)}
                    className="bg-secondary/40"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Mensagem principal</Label>
                <Textarea
                  data-testid="ty-body"
                  value={settings.thankyou?.body_text || ""}
                  onChange={(e) => setThankyou("body_text", e.target.value)}
                  rows={3}
                  className="bg-secondary/40"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">
                    Número WhatsApp (com DDI+DDD, só números)
                  </Label>
                  <Input
                    data-testid="ty-whatsapp-number"
                    placeholder="Ex: 5571999999999"
                    value={settings.thankyou?.whatsapp_number || ""}
                    onChange={(e) => setThankyou("whatsapp_number", e.target.value)}
                    className="bg-secondary/40 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">
                    OU link de grupo / convite (sobrepõe o número)
                  </Label>
                  <Input
                    data-testid="ty-whatsapp-url"
                    placeholder="https://chat.whatsapp.com/..."
                    value={settings.thankyou?.whatsapp_url || ""}
                    onChange={(e) => setThankyou("whatsapp_url", e.target.value)}
                    className="bg-secondary/40 font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Mensagem pré-preenchida no WhatsApp</Label>
                <Textarea
                  data-testid="ty-whatsapp-message"
                  value={settings.thankyou?.whatsapp_message || ""}
                  onChange={(e) => setThankyou("whatsapp_message", e.target.value)}
                  rows={2}
                  className="bg-secondary/40"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Texto do botão</Label>
                  <Input
                    data-testid="ty-button-text"
                    value={settings.thankyou?.button_text || ""}
                    onChange={(e) => setThankyou("button_text", e.target.value)}
                    className="bg-secondary/40"
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30 mt-6">
                  <div>
                    <p className="text-sm font-medium">Exibir token de autenticidade</p>
                    <p className="text-xs text-muted-foreground">
                      Recomendado para comprovar originalidade.
                    </p>
                  </div>
                  <Switch
                    data-testid="ty-show-token"
                    checked={settings.thankyou?.show_token !== false}
                    onCheckedChange={(v) => setThankyou("show_token", v)}
                  />
                </div>
              </div>

              <Button
                onClick={save}
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-thankyou-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Salvar página de obrigado</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-display">Alterar senha do painel</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={changePassword} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Senha atual</Label>
                  <Input
                    data-testid="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="bg-secondary/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Nova senha</Label>
                  <Input
                    data-testid="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-secondary/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Confirmar nova senha</Label>
                  <Input
                    data-testid="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-secondary/40"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={pwSaving}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="change-password-btn"
                >
                  {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Alterar senha"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
